/**
 * identity-service session bootstrap (otter-camp-suite) — rehydrate-on-load.
 *
 * Mirrors dao-suite's `identity-code-bootstrap`. Runs BEFORE the app's auth gate
 * (the `AuthCTA` overlay, gated on `checkSession()`) evaluates, so a logged-in
 * DAO member who navigates to Otter Camp stays authenticated instead of being
 * bounced to login.
 *
 * ## Why this must run before the auth gate
 *
 * The identity-service JWT store (`identity-jwt-store`) is **in-memory only** and
 * is empty on every fresh page load. Otter Camp's auth gate decides whether to
 * show the login CTA by calling `checkSession()` once on mount. If the gate runs
 * before the session is rehydrated, it finds no session and shows the login CTA —
 * the cross-suite "bounce to login" bug this module fixes.
 *
 * The bootstrap therefore settles the identity-service session first (App holds
 * the page behind a loader while the bootstrap promise is pending), so the JWT is
 * already in the store when `checkSession()` runs.
 *
 * ## Two entry paths, one single-flight promise
 *
 *   - `?code=` present (login return) → exchange the one-time code for a JWT and
 *     strip the consumed code from the URL.
 *   - no `?code=` (plain load / cross-suite navigation) → silent
 *     {@link refreshIdentityJwt} from the shared httpOnly `hwdao_refresh` cookie
 *     (Domain=.helloworlddao.com — sent to `ottercamp.*.helloworlddao.com`). On
 *     success the fresh JWT is stored (session rehydrated, member authenticated);
 *     on failure the app proceeds unauthenticated (genuine guest — same outcome
 *     as before this fix).
 *
 * ## Single-flight under StrictMode
 *
 * The bootstrap is a module-singleton cached promise: React StrictMode's
 * double-invoked effects (and any re-render) await the SAME promise, so the
 * single-use code is exchanged — or the refresh attempted — exactly once.
 */

import { setIdentityJwt } from './identity-jwt-store';
import {
  CODE_PARAM,
  exchangeCodeForJwt,
  refreshIdentityJwt,
} from './identity-service-client';

/**
 * True when the current URL carries a front-door one-time `?code=` (login
 * return). Retained as a pure URL probe for callers that need to distinguish a
 * login return from a plain load without running the bootstrap.
 */
export function identityCodeExchangePending(): boolean {
  try {
    return new URLSearchParams(window.location.search).has(CODE_PARAM);
  } catch {
    return false;
  }
}

/**
 * Replace the current history entry with `nextUrl` (no new entry, preserving
 * history state). Used to drop the consumed one-time code from the address bar.
 */
function replaceUrl(nextUrl: string): void {
  try {
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // history API unavailable — non-fatal; the code is single-use + already
    // consumed server-side, so a lingering param is cosmetic only.
  }
}

/** Strip the one-time `code` param from the current URL, preserving path/other params/hash. */
function urlWithoutCode(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(CODE_PARAM);
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
}

/** Module-singleton in-flight (then settled) bootstrap promise. */
let bootstrapPromise: Promise<void> | null = null;

/**
 * Settle the identity-service session before the auth gate evaluates.
 *
 * Idempotent + single-flight: repeated calls (incl. StrictMode double-invoke)
 * return the SAME cached promise. Resolves (never rejects) once the JWT store +
 * URL are settled, so the caller can render the app.
 */
export function runIdentityCodeBootstrap(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    let code: string | null = null;
    try {
      code = new URLSearchParams(window.location.search).get(CODE_PARAM);
    } catch {
      code = null;
    }

    // Plain load / cross-suite navigation (no login-return code): the in-memory
    // JWT store is empty after a navigation/refresh, but the shared httpOnly
    // refresh cookie may still be valid. Attempt a silent refresh to rehydrate
    // the session; on failure proceed unauthenticated (genuine guest). No URL
    // reconciliation here: there is no code to strip.
    if (!code) {
      const jwt = await refreshIdentityJwt();
      if (jwt) setIdentityJwt(jwt);
      return;
    }

    const jwt = await exchangeCodeForJwt(code);
    if (jwt) setIdentityJwt(jwt);
    // Strip the consumed code regardless of success: it is single-use + already
    // consumed server-side and must never linger in the address bar / history.
    // On failure the app proceeds on the cookie session (backward-compatible).
    replaceUrl(urlWithoutCode());
  })();

  return bootstrapPromise;
}

/** Test-only: reset the single-flight bootstrap promise between cases. */
export function resetIdentityCodeBootstrap(): void {
  bootstrapPromise = null;
}
